import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    Code,
    Facebook,
    GitHub,
    InfoOutlined,
    Instagram,
    Language,
    LinkedIn,
} from "@mui/icons-material";

export default function AboutDialog({ open, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>About TextLab</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <Card variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Stack
                                direction="row"
                                spacing={1.2}
                                alignItems="center">
                                <InfoOutlined
                                    color="primary"
                                    sx={{ fontSize: 22 }}
                                />
                                <Typography
                                    variant="h6"
                                    fontWeight={800}
                                    sx={{ lineHeight: 1 }}>
                                    About TextLab
                                </Typography>
                            </Stack>

                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                sx={{ mt: 2 }}
                                alignItems="flex-start">
                                <Box
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: (theme) =>
                                            theme.palette.mode === "dark"
                                                ? "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))"
                                                : "linear-gradient(135deg, rgba(0,0,0,0.04), rgba(0,0,0,0.01))",
                                        border: "1px solid",
                                        borderColor: "divider",
                                        flexShrink: 0,
                                    }}>
                                    <Box
                                        component="img"
                                        src={`${process.env.PUBLIC_URL}/logo.png`}
                                        alt="TextLab Logo"
                                        sx={{
                                            width: 38,
                                            height: 38,
                                            objectFit: "contain",
                                        }}
                                    />
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            lineHeight: 1.8,
                                            textAlign: "justify",
                                        }}>
                                        TextLab is a local-first workspace
                                        designed for focused writing and
                                        structured organization. It combines a
                                        simple folder system with a
                                        distraction-free editor, giving you a
                                        fast and reliable environment to capture
                                        and manage ideas. No accounts, no
                                        syncing, no background services.
                                        Everything runs directly in your
                                        browser. Your data stays local and fully
                                        under your control.
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 3,
                            background: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0))"
                                    : "linear-gradient(135deg, rgba(0,0,0,0.02), rgba(0,0,0,0))",
                        }}>
                        <CardContent>
                            <Stack
                                direction="row"
                                spacing={1.2}
                                alignItems="center">
                                <Code color="primary" sx={{ fontSize: 22 }} />
                                <Typography
                                    variant="h6"
                                    fontWeight={800}
                                    sx={{ lineHeight: 1 }}>
                                    Developer profile
                                </Typography>
                            </Stack>

                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                sx={{ mt: 2 }}
                                alignItems={{ xs: "flex-start", sm: "center" }}>
                                <Avatar
                                    src={`${process.env.PUBLIC_URL}/profile.jpg`}
                                    alt="Developer"
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        border: "2px solid",
                                        borderColor: "divider",
                                    }}
                                />

                                <Box sx={{ flex: 1 }}>
                                    <Typography fontWeight={800}>
                                        Bilal Abdulhadi
                                    </Typography>
                                    <Chip
                                        label="Web Developer"
                                        size="small"
                                        sx={{ mt: 0.5, fontWeight: 600 }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            mt: 1,
                                            lineHeight: 1.7,
                                            textAlign: "justify",
                                        }}>
                                        Computer engineer and web developer
                                        focused on building reliable,
                                        user-friendly platforms and digital
                                        products. Passionate about clean
                                        interfaces, thoughtful UX, and scalable
                                        engineering that turns ideas into
                                        real-world impact.
                                    </Typography>
                                </Box>
                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Tooltip title="Website">
                                    <IconButton
                                        component="a"
                                        href="https://bilalabdulhadi.com"
                                        target="_blank"
                                        rel="noreferrer">
                                        <Language />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="GitHub">
                                    <IconButton
                                        component="a"
                                        href="https://github.com/bilalabdulhadii"
                                        target="_blank"
                                        rel="noreferrer">
                                        <GitHub />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="LinkedIn">
                                    <IconButton
                                        component="a"
                                        href="https://linkedin.com/in/bilalabdulhadii"
                                        target="_blank"
                                        rel="noreferrer">
                                        <LinkedIn />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Instagram">
                                    <IconButton
                                        component="a"
                                        href="https://instagram.com/bilalabdulhadii"
                                        target="_blank"
                                        rel="noreferrer">
                                        <Instagram />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Facebook">
                                    <IconButton
                                        component="a"
                                        href="https://facebook.com/bilalabdulhadii"
                                        target="_blank"
                                        rel="noreferrer">
                                        <Facebook />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
}
